/**
 * ARMORE B2G2 free-key allocation for one-page UI.
 * Production helper consumed by objkt-bundle-single-b2g2.
 *
 * IMPORTANT: These heuristics only drive UI free badges. They are NOT Shopify checkout
 * enforcement. Live cart/Admin discounts remain authoritative.
 *
 * Policies:
 * - 'fixed_roles' (V14.1 / Nico-approved): free products are configured by stable
 *   product id / handle lists (Bracelet + Earrings). Paid products are Ring + Stacking Band.
 *   Allocation does NOT depend on comparative prices — Stacking Band can become cheaper
 *   without becoming free. Marketing FREE on free-role cards is separate (section Liquid/JS).
 * - 'deluxe_set': when a true Ring is selected at 4/4, mark Bracelet/Earring selections free.
 * - 'cheapest': free the N lowest variant.price selections (legacy V14).
 * - 'selection_order': free the last N selections.
 */
(function (global) {
  'use strict';

  function classifySelection(sel) {
    var blob = [
      sel && sel.title,
      sel && sel.handle,
      sel && sel.productType,
      sel && sel.key,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    if (/\bearrings?\b/.test(blob) || blob.indexOf('earring') !== -1) return 'earrings';
    if (/\bbracelets?\b/.test(blob) || blob.indexOf('bracelet') !== -1) return 'bracelet';
    if (blob.indexOf('stacking') !== -1) return 'band';
    if (/\bband\b/.test(blob)) return 'band';
    if (/\bring\b/.test(blob)) return 'ring';
    return 'other';
  }

  function sortByVariantPrice(a, b) {
    var priceA = Number(a && a.variant && a.variant.price) || 0;
    var priceB = Number(b && b.variant && b.variant.price) || 0;
    if (priceA !== priceB) return priceA - priceB;
    return String(a.key || '').localeCompare(String(b.key || ''));
  }

  function normalizeList(value) {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value
        .map(function (v) { return String(v == null ? '' : v).trim(); })
        .filter(Boolean);
    }
    return String(value)
      .split(/[\n,]+/)
      .map(function (v) { return v.trim(); })
      .filter(Boolean);
  }

  function toIdSet(list) {
    var set = new Set();
    normalizeList(list).forEach(function (v) { set.add(String(v)); });
    return set;
  }

  function toHandleSet(list) {
    var set = new Set();
    normalizeList(list).forEach(function (v) { set.add(String(v).toLowerCase()); });
    return set;
  }

  function isFreeRoleSelection(sel, freeIds, freeHandles) {
    if (!sel) return false;
    if (freeIds.size && freeIds.has(String(sel.id || sel.key || ''))) return true;
    if (freeHandles.size && sel.handle && freeHandles.has(String(sel.handle).toLowerCase())) return true;
    // Fallback classification only when no explicit config was provided
    if (!freeIds.size && !freeHandles.size) {
      var kind = classifySelection(sel);
      return kind === 'bracelet' || kind === 'earrings';
    }
    return false;
  }

  function isPaidRoleSelection(sel, paidIds, paidHandles) {
    if (!sel) return false;
    if (paidIds.size && paidIds.has(String(sel.id || sel.key || ''))) return true;
    if (paidHandles.size && sel.handle && paidHandles.has(String(sel.handle).toLowerCase())) return true;
    if (!paidIds.size && !paidHandles.size) {
      var kind = classifySelection(sel);
      return kind === 'ring' || kind === 'band';
    }
    return false;
  }

  /**
   * Marketing / card role: is this product configured as a fixed free item?
   * Independent of selection count — used for initial FREE labels.
   */
  function isConfiguredFreeProduct(productRef, options) {
    var opts = options || {};
    var freeIds = toIdSet(opts.freeProductIds);
    var freeHandles = toHandleSet(opts.freeProductHandles);
    var ref = {
      id: productRef && (productRef.id != null ? productRef.id : productRef.key),
      key: productRef && productRef.key,
      handle: productRef && productRef.handle,
      title: productRef && productRef.title,
    };
    return isFreeRoleSelection(ref, freeIds, freeHandles);
  }

  /**
   * Qualifying complete set: exactly one Ring + one Stacking Band + one Bracelet + one Earrings.
   * Enforced in the builder UI. Native Shopify BXGY may still allow other cart combinations.
   */
  function isQualifyingFixedSet(selections, options) {
    var opts = options || {};
    var list = Array.isArray(selections) ? selections.filter(Boolean) : [];
    var maxItems = opts.maxItems != null ? opts.maxItems : 4;
    if (list.length !== maxItems) return false;

    var freeIds = toIdSet(opts.freeProductIds);
    var freeHandles = toHandleSet(opts.freeProductHandles);
    var paidIds = toIdSet(opts.paidProductIds);
    var paidHandles = toHandleSet(opts.paidProductHandles);

    var rings = 0;
    var bands = 0;
    var bracelets = 0;
    var earrings = 0;
    var other = 0;

    for (var i = 0; i < list.length; i++) {
      var sel = list[i];
      var kind = classifySelection(sel);
      var configuredFree = isFreeRoleSelection(sel, freeIds, freeHandles);
      var configuredPaid = isPaidRoleSelection(sel, paidIds, paidHandles);

      if (kind === 'ring' && configuredPaid) rings += 1;
      else if (kind === 'band' && configuredPaid) bands += 1;
      else if (kind === 'bracelet' && configuredFree) bracelets += 1;
      else if (kind === 'earrings' && configuredFree) earrings += 1;
      else other += 1;
    }

    return rings === 1 && bands === 1 && bracelets === 1 && earrings === 1 && other === 0;
  }

  function getDiscountedKeysForUi(selections, options) {
    var opts = options || {};
    var maxItems = opts.maxItems != null ? opts.maxItems : 4;
    var freeCount = opts.freeCount != null ? opts.freeCount : 2;
    var policy = opts.policy || 'deluxe_set';
    var list = Array.isArray(selections) ? selections : [];
    var filled = list.filter(Boolean);

    if (policy === 'fixed_roles') {
      var freeIds = toIdSet(opts.freeProductIds);
      var freeHandles = toHandleSet(opts.freeProductHandles);
      // Summary Free labels for selected free-role products (any progress).
      // Price independence: never use variant.price to choose free items.
      return new Set(
        filled
          .filter(function (sel) {
            return isFreeRoleSelection(sel, freeIds, freeHandles);
          })
          .map(function (sel) {
            return sel.key;
          })
      );
    }

    if (list.length !== maxItems) return new Set();
    if (filled.length < freeCount) return new Set();

    if (policy === 'selection_order') {
      return new Set(filled.slice(-freeCount).map(function (sel) { return sel.key; }));
    }

    if (policy === 'deluxe_set') {
      var hasRing = filled.some(function (sel) {
        return classifySelection(sel) === 'ring';
      });
      if (!hasRing) return new Set();

      var getPool = filled.filter(function (sel) {
        var kind = classifySelection(sel);
        return kind === 'bracelet' || kind === 'earrings';
      });
      var sortedGets = getPool.slice().sort(sortByVariantPrice);
      return new Set(sortedGets.slice(0, freeCount).map(function (sel) { return sel.key; }));
    }

    var sorted = filled.slice().sort(sortByVariantPrice);
    return new Set(sorted.slice(0, freeCount).map(function (sel) { return sel.key; }));
  }

  global.ArmoreB2G2FreeKeys = {
    getDiscountedKeysForUi: getDiscountedKeysForUi,
    classifySelection: classifySelection,
    isConfiguredFreeProduct: isConfiguredFreeProduct,
    isQualifyingFixedSet: isQualifyingFixedSet,
    POLICIES: {
      FIXED_ROLES: 'fixed_roles',
      DELUXE_SET: 'deluxe_set',
      CHEAPEST: 'cheapest',
      SELECTION_ORDER: 'selection_order',
    },
  };
})(typeof window !== 'undefined' ? window : globalThis);
